import {
  ETIQUETA_RESULTADO_ELEMENTO,
  ETIQUETA_RESULTADO_ELEMENTO_EN,
  type ElementoDelLote,
  type ResultadoDelLote,
} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {et, tr} from '../i18n/idioma';
import {camposDe, type CambiosAceptados} from '../utilidades/cambios-aceptados';
import {numero} from '../utilidades/formato';

type Props = {
  resultado: ResultadoDelLote;
  className?: string;
  // Si viene, cada cambio tiene su casilla para aceptarlo o no.
  aceptados?: CambiosAceptados;
  onAceptados?: (aceptados: CambiosAceptados) => void;
};

const TONO: Record<ElementoDelLote['resultado'], string> = {
  'sin cambios': 'bg-papel text-apagado',
  cambia: 'bg-verde-claro text-verde-oscuro',
  nueva: 'bg-dorado-claro text-tinta',
  error: 'bg-red-50 text-alerta',
  aplicado: 'bg-verde-claro text-verde-oscuro',
  creado: 'bg-dorado-claro text-tinta',
  omitido: 'bg-red-50 text-alerta',
};

// Lo que un lote cambiaria (o cambio) en cada exportacion: chips de resumen
// y una tabla con los campos, el valor anterior y el nuevo.
export function ResultadoDeLote({resultado, className = '', aceptados, onAceptados}: Props) {
  const [soloConNovedad, setSoloConNovedad] = useState(true);
  const eligiendo = Boolean(aceptados && onAceptados);
  const marcar = (codigo: string, campos: string[], si: boolean) => {
    if (!aceptados || !onAceptados) return;
    const nuevos = new Map(aceptados);
    const actuales = new Set(nuevos.get(codigo) ?? []);
    for (const campo of campos) {
      if (si) actuales.add(campo);
      else actuales.delete(campo);
    }
    if (actuales.size) nuevos.set(codigo, actuales);
    else nuevos.delete(codigo);
    onAceptados(nuevos);
  };
  const r = resultado.resumen;
  const aplicado = resultado.modo === 'aplicar';
  const chips: Array<[string, string]> = [
    [tr('Exportaciones', 'Exports'), numero(r.exportaciones)],
    [aplicado ? tr('Actualizadas', 'Updated') : tr('Cambian', 'Changing'), numero(r.conCambios)],
    [aplicado ? tr('Creadas', 'Created') : tr('Nuevas', 'New'), numero(r.nuevas)],
    [tr('Sin cambios', 'No changes'), numero(r.sinCambios)],
    [
      aplicado ? tr('Omitidas por errores', 'Skipped due to errors') : tr('Con errores', 'With errors'),
      numero(r.conErrores),
    ],
    [tr('Campos', 'Fields'), numero(r.camposCambiados)],
  ];
  const visibles = soloConNovedad
    ? resultado.elementos.filter(x => x.resultado !== 'sin cambios')
    : resultado.elementos;
  return (
    <div className={className}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        {chips.map(([nombre, valor]) => (
          <div key={nombre} className="rounded-md border border-linea bg-papel px-3 py-1.5">
            <div className="text-[11px] text-apagado">{nombre}</div>
            <div className="text-sm font-semibold tabular-nums text-verde-oscuro">{valor}</div>
          </div>
        ))}
      </div>
      {aplicado && (
        <p className="mt-2 text-sm text-verde-oscuro">
          {tr(
            `Listo: ${numero(r.aplicadas)} exportaciones actualizadas`,
            `Done: ${numero(r.aplicadas)} exports updated`,
          )}
          {r.omitidas
            ? tr(` y ${numero(r.omitidas)} omitidas por errores`, ` and ${numero(r.omitidas)} skipped due to errors`)
            : ''}
          .
        </p>
      )}
      <label className="mt-2 flex items-center gap-2 text-xs text-apagado">
        <input type="checkbox" checked={soloConNovedad} onChange={e => setSoloConNovedad(e.target.checked)} />
        {tr('Mostrar solo las que cambian o tienen errores', 'Show only those that change or have errors')}
      </label>
      <div className="mt-2 max-h-[28rem] overflow-auto rounded-lg border border-linea bg-white">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-papel">
            <tr className="text-left text-xs uppercase tracking-wide text-apagado">
              <th className="px-3 py-2 font-semibold">{tr('Exportación', 'Export')}</th>
              <th className="px-3 py-2 font-semibold">{tr('Resultado', 'Result')}</th>
              <th className="px-3 py-2 font-semibold">
                {tr('Cambios (anterior → nuevo)', 'Changes (previous → new)')}
              </th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((x, i) => (
              <tr key={`${x.codigo}-${i}`} className="border-t border-linea/70 align-top">
                <td className="px-3 py-1.5 font-medium whitespace-nowrap">
                  {eligiendo && camposDe(x).length > 0 && (
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={camposDe(x).every(c => aceptados!.get(x.codigo)?.has(c))}
                      onChange={e => marcar(x.codigo, camposDe(x), e.target.checked)}
                      aria-label={tr(`Aceptar todos los cambios de ${x.codigo}`, `Accept every change of ${x.codigo}`)}
                    />
                  )}
                  <Link
                    to={`/seguimiento/${encodeURIComponent(x.codigoNuevo ?? x.codigo)}`}
                    className="text-verde hover:underline"
                  >
                    {x.codigo}
                  </Link>
                  {x.codigoNuevo && <span className="text-apagado"> → {x.codigoNuevo}</span>}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${TONO[x.resultado]}`}>
                    {et(ETIQUETA_RESULTADO_ELEMENTO, ETIQUETA_RESULTADO_ELEMENTO_EN)[x.resultado]}
                  </span>
                </td>
                <td className="px-3 py-1.5">
                  {x.errores.length > 0 && (
                    <ul className="text-alerta">
                      {x.errores.map(e => (
                        <li key={e}>✕ {e}</li>
                      ))}
                    </ul>
                  )}
                  {x.cambios.length > 0 && (
                    <ul className="grid gap-x-4 gap-y-0.5 md:grid-cols-2">
                      {x.cambios.map(c => (
                        <li key={c.campo} className="flex flex-wrap items-center gap-1">
                          {eligiendo && !x.errores.length && (
                            <input
                              type="checkbox"
                              checked={aceptados!.get(x.codigo)?.has(c.campo) ?? false}
                              onChange={e => marcar(x.codigo, [c.campo], e.target.checked)}
                              aria-label={tr(
                                `Aceptar ${c.etiqueta} de ${x.codigo}`,
                                `Accept ${c.etiqueta} of ${x.codigo}`,
                              )}
                            />
                          )}
                          <span className="text-apagado">{c.etiqueta}:</span>
                          <span className="line-through decoration-alerta/60">{c.antes ?? '—'}</span>
                          <span>→</span>
                          <span className="font-medium">{c.despues ?? '—'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {x.avisos.length > 0 && (
                    <ul className="text-dorado" data-prueba="avisos-del-lote">
                      {x.avisos.map(a => (
                        <li key={a}>⚠ {a}</li>
                      ))}
                    </ul>
                  )}
                  {!x.errores.length && !x.cambios.length && !x.avisos.length && (
                    <span className="text-apagado">—</span>
                  )}
                </td>
              </tr>
            ))}
            {!visibles.length && (
              <tr>
                <td colSpan={3} className="px-3 py-6 text-center text-apagado">
                  {tr('Nada que mostrar.', 'Nothing to show.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
